import { useEffect, useRef, useState } from 'react';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError } from '@/auth/api';
import { PrimaryButton } from '@/components/PrimaryButton';
import { getCustomers } from '@/features/customers/api';
import { NewCustomerDialog } from '@/features/customers/components/NewCustomerDialog';
import type { CustomerAccountStatus } from '@/features/customers/types';
import { changeQuotationAccount } from '@/features/operations/api';
import type { QuotationAccount, QuotationRequestItem } from '@/features/operations/types';
import { StatusChip } from '@/components/StatusChip';

/// What the dialog needs of an account to offer it and to confirm it.
type AccountChoice = { id: string; fullName: string; email: string; status: CustomerAccountStatus };

type SearchState =
  | { status: 'idle' }
  | { status: 'searching' }
  | { status: 'error' }
  | { status: 'done'; customers: AccountChoice[]; total: number };

/// Reasons of the backend that mean "this quotation can no longer move": the page reloads it.
const conflictReasons = new Set(['WEB_QUOTATION', 'ACCOUNT_ACTIVE', 'CONCURRENT_CHANGE']);

/// Names and mails can be long; they wrap instead of pushing the dialog wider.
const wrap = { overflowWrap: 'anywhere' } as const;

function AccountLine({ account, label }: { account: AccountChoice; label: string }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0.5, sm: 1 }}
        alignItems={{ sm: 'center' }}
      >
        <Typography variant="body1" fontWeight={600} sx={wrap}>
          {account.fullName}
        </Typography>
        <Box>
          <StatusChip domain="customerAccount" status={account.status} />
        </Box>
      </Stack>
      {/* As visible as the name: when both accounts share it (a mistyped mail, the usual case),
          the mail is the only thing that tells them apart (U-2). */}
      <Typography variant="body2" fontWeight={600} sx={wrap}>
        {account.email}
      </Typography>
    </Box>
  );
}

/**
 * Moves a counter quotation whose account is still pending to the right account (#108): the
 * mail was mistyped at the counter. Two steps, so nothing moves by accident: choose the account
 * (search, or create one, which only selects it) and then confirm, seeing both accounts.
 *
 * A 409 that means the quotation can no longer move (somebody corrected it, or its account was
 * activated meanwhile) goes back to the page through `onConflict`, which reloads the detail.
 */
export function CorrectQuotationAccountDialog({
  token,
  open,
  quotation,
  currentAccount,
  onClose,
  onChanged,
  onConflict,
}: {
  token: string;
  open: boolean;
  quotation: QuotationRequestItem;
  currentAccount: QuotationAccount;
  onClose: () => void;
  onChanged: (updated: QuotationRequestItem, previousInvitationRevoked: boolean) => void;
  onConflict: () => void;
}) {
  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState<SearchState>({ status: 'idle' });
  const [selected, setSelected] = useState<AccountChoice | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'info'; text: string } | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /// The latest search launched. An answer that belongs to an older one is dropped, so it can
  /// never replace the results of what is being searched now (review of #244).
  const latestSearch = useRef(0);

  const runSearch = async (term: string) => {
    const trimmed = term.trim();
    const searchId = ++latestSearch.current;

    if (!trimmed) {
      setSearch({ status: 'idle' });
      return;
    }

    setSearch({ status: 'searching' });

    try {
      const page = await getCustomers(token, { search: trimmed });

      if (searchId === latestSearch.current) {
        setSearch({ status: 'done', customers: page.customers, total: page.total });
      }
    } catch {
      if (searchId === latestSearch.current) {
        setSearch({ status: 'error' });
      }
    }
  };

  const isSearching = search.status === 'searching';

  /// Enter and the button do the same, and neither starts another search while one is running.
  const searchTyped = () => {
    if (!isSearching && searchInput.trim()) {
      void runSearch(searchInput);
    }
  };

  // Every opening starts over, searching by the name the counter typed: the right account is
  // usually the same person with another mail.
  useEffect(() => {
    if (!open) {
      return;
    }

    setStep('choose');
    setSearchInput(quotation.contact.name);
    setSelected(null);
    setNotice(null);
    setSaveError(null);
    void runSearch(quotation.contact.name);
    // Only on opening: the search box is the employee's after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    if (!isSaving) {
      onClose();
    }
  };

  /// The new account is selected, never moved to: the employee still confirms it.
  const handleCreated = (customer: AccountChoice, created: boolean) => {
    setIsCreating(false);

    if (customer.id === currentAccount.id) {
      setNotice({
        severity: 'info',
        text: 'Ese mail es el de la cuenta en la que ya está la cotización. Buscá o creá otra.',
      });
      return;
    }

    setSelected(customer);
    setSearch((current) =>
      current.status === 'done'
        ? {
            ...current,
            customers: [customer, ...current.customers.filter((item) => item.id !== customer.id)],
          }
        : { status: 'done', customers: [customer], total: 1 },
    );
    setNotice({
      severity: 'success',
      text: `${
        created
          ? `Creamos la cuenta de ${customer.fullName}, le mandamos la invitación y quedó elegida. La cuenta queda creada aunque canceles.`
          : `Ese mail ya tenía cuenta: elegimos la de ${customer.fullName}.`
      } La cotización todavía no se movió: continuá para confirmarlo.`,
    });
  };

  const save = async () => {
    if (!selected) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const result = await changeQuotationAccount(token, quotation.id, selected.id);
      onChanged(result.quotation, result.previousInvitationRevoked);
    } catch (error) {
      const reason =
        error instanceof ApiError && typeof error.details?.reason === 'string'
          ? error.details.reason
          : null;

      if (
        error instanceof ApiError &&
        error.status === 409 &&
        reason &&
        conflictReasons.has(reason)
      ) {
        onConflict();
      } else if (reason === 'TARGET_DEACTIVATED') {
        setSaveError('La cuenta elegida está desactivada. Volvé y elegí otra.');
      } else if (error instanceof ApiError && error.status === 400) {
        setSaveError('Esa cuenta ya no se puede elegir. Volvé y buscala de nuevo.');
      } else {
        setSaveError('No pudimos mover la cotización. Revisá la conexión e intentá de nuevo.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const results = search.status === 'done' ? search.customers : [];

  return (
    <>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        {step === 'choose' ? (
          <>
            <DialogTitle>Corregir la cuenta de la cotización</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <DialogContentText>
                  Elegí la cuenta correcta de la persona. Antes de moverla te pedimos que lo
                  confirmes.
                </DialogContentText>
                <AccountLine account={currentAccount} label="Cuenta actual" />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Buscar cliente"
                    placeholder="Nombre, mail o teléfono"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        searchTyped();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={searchTyped}
                    disabled={isSearching || !searchInput.trim()}
                    sx={{ flexShrink: 0 }}
                  >
                    {isSearching ? 'Buscando...' : 'Buscar'}
                  </Button>
                </Stack>

                {notice ? <Alert severity={notice.severity}>{notice.text}</Alert> : null}
                {search.status === 'error' ? (
                  <Alert severity="error">
                    No pudimos buscar clientes. Revisá la conexión e intentá de nuevo.
                  </Alert>
                ) : null}
                {search.status === 'idle' ? (
                  <Typography variant="body2" color="text.secondary">
                    Buscá por nombre, mail o teléfono.
                  </Typography>
                ) : null}
                {search.status === 'done' && !results.length ? (
                  <Alert severity="info">
                    No encontramos clientes con esa búsqueda. Probá con otro dato o creá una cuenta
                    nueva.
                  </Alert>
                ) : null}
                {results.length ? (
                  <List
                    dense
                    disablePadding
                    aria-label="Clientes encontrados"
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                  >
                    {results.map((customer) => {
                      const isCurrent = customer.id === currentAccount.id;
                      const isSelected = selected?.id === customer.id;

                      return (
                        <ListItemButton
                          key={customer.id}
                          divider
                          selected={isSelected}
                          disabled={isCurrent}
                          aria-pressed={isSelected}
                          onClick={() => {
                            setSelected(customer);
                            setNotice(null);
                          }}
                        >
                          <Stack spacing={0.25} sx={{ minWidth: 0, width: '100%' }}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={{ xs: 0.5, sm: 1 }}
                              alignItems={{ sm: 'center' }}
                            >
                              <Typography variant="body2" fontWeight={600} sx={wrap}>
                                {customer.fullName}
                              </Typography>
                              <Box>
                                <StatusChip domain="customerAccount" status={customer.status} />
                              </Box>
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={wrap}>
                              {customer.email}
                              {isCurrent ? ' · cuenta actual de la cotización' : ''}
                            </Typography>
                          </Stack>
                        </ListItemButton>
                      );
                    })}
                  </List>
                ) : null}
                {search.status === 'done' && search.total > results.length ? (
                  <Typography variant="caption" color="text.secondary">
                    Mostramos {results.length} de {search.total}. Si no está, afiná la búsqueda.
                  </Typography>
                ) : null}
                <Box>
                  <Button
                    variant="text"
                    color="secondary"
                    startIcon={<PersonAddAlt1Icon />}
                    onClick={() => setIsCreating(true)}
                  >
                    Crear una cuenta nueva
                  </Button>
                </Box>
                {/* What "Continuar" moves the quotation to, always in sight: a new search can leave
                    the chosen account out of the results, and the choice must never stay active
                    without being seen (review of #244). */}
                {selected ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    sx={{
                      border: 1,
                      borderColor: 'secondary.main',
                      borderRadius: 1,
                      px: 2,
                      py: 1.5,
                    }}
                    role="region"
                    aria-label="Cuenta elegida"
                  >
                    <AccountLine account={selected} label="Cuenta elegida" />
                    <Box>
                      <Button color="inherit" size="small" onClick={() => setSelected(null)}>
                        Quitar
                      </Button>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Todavía no elegiste ninguna cuenta.
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button color="inherit" onClick={close}>
                Cancelar
              </Button>
              <PrimaryButton disabled={!selected} onClick={() => setStep('confirm')}>
                Continuar
              </PrimaryButton>
            </DialogActions>
          </>
        ) : null}

        {step === 'confirm' && selected ? (
          <>
            <DialogTitle>¿Mover la cotización a esta cuenta?</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <AccountLine account={currentAccount} label="De" />
                <Typography aria-hidden color="text.secondary">
                  ↓
                </Typography>
                <AccountLine account={selected} label="A" />
                <DialogContentText sx={wrap}>
                  El contacto original de la solicitud no cambia: sigue siendo{' '}
                  {quotation.contact.name} · {quotation.contact.email} · {quotation.contact.phone}.
                  El cambio queda en el historial de la cotización con tu usuario y la fecha.
                </DialogContentText>
                {/* Every correction voids it (alternative 1, decided 2026-10-26). */}
                <DialogContentText sx={wrap}>
                  La invitación que le mandamos a {currentAccount.email} deja de funcionar. La
                  cuenta no se borra: si ese mail es de alguien, reenviale la invitación desde su
                  ficha.
                </DialogContentText>
                {selected.status !== 'PENDING' ? (
                  <Alert severity="warning">
                    La cuenta elegida ya está activa: después de este cambio, la cotización queda de
                    esa persona y no se va a poder volver a corregir.
                  </Alert>
                ) : null}
                {saveError ? <Alert severity="error">{saveError}</Alert> : null}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                color="inherit"
                disabled={isSaving}
                onClick={() => {
                  setSaveError(null);
                  setStep('choose');
                }}
              >
                Volver a elegir
              </Button>
              <PrimaryButton onClick={() => void save()} disabled={isSaving}>
                {isSaving ? 'Guardando...' : 'Mover la cotización'}
              </PrimaryButton>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <NewCustomerDialog
        token={token}
        open={isCreating}
        onClose={() => setIsCreating(false)}
        onDone={handleCreated}
      />
    </>
  );
}
